/**
 * StrainCell Component
 *
 * Displays demand vs supply for a single strain family within a single week.
 * Shows shadow batches awaiting approval and real batches in production,
 * grouped by individual strain within the family.
 * Supports demand overrides via inline popover.
 *
 * US-C2-006: Planner Page UI
 * US-C2-008: Demand Override Support
 * Strain Families: shows family-level demand with per-strain batch grouping
 */

import { useState, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card'
import BatchCard from './BatchCard'
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Plus, Pencil, X, RotateCw, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/supabase-client'

export default function StrainCell({ familyData, weekStart, override, onSetOverride, onRemoveOverride, onAddBatch, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [overrideKg, setOverrideKg] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [demandBreakdown, setDemandBreakdown] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)

  const {
    familyId,
    familyName,
    familyCode,
    demandKg,
    supplyKg,
    gapKg,
    shadowBatches = [],
    realBatches = []
  } = familyData

  // Use override value if present, otherwise use forecast demand
  const effectiveDemandKg = override ? Number(override.override_kg) : demandKg
  const effectiveGapKg = effectiveDemandKg - supplyKg
  const hasGap = effectiveGapKg > 0
  const hasSurplus = effectiveGapKg < 0
  const batchCount = shadowBatches.length + realBatches.length

  // Group batches by individual strain for display
  const batchesByStrain = {}
  for (const batch of [...shadowBatches, ...realBatches]) {
    const strainName = batch.strains?.name || 'Unknown'
    if (!batchesByStrain[strainName]) {
      batchesByStrain[strainName] = []
    }
    batchesByStrain[strainName].push(batch)
  }

  const handleOpenOverride = () => {
    setOverrideKg(override ? override.override_kg.toString() : demandKg.toFixed(1))
    setOverrideReason(override?.reason || '')
    setOverrideOpen(true)
  }

  const handleSaveOverride = async () => {
    const kg = parseFloat(overrideKg)
    if (isNaN(kg) || kg < 0) return

    setSaving(true)
    try {
      await onSetOverride?.(familyId, weekStart, kg, overrideReason || null)
      setOverrideOpen(false)
    } catch {
      // Error handled in hook
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveOverride = async () => {
    setSaving(true)
    try {
      await onRemoveOverride?.(familyId, weekStart)
      setOverrideOpen(false)
    } catch {
      // Error handled in hook
    } finally {
      setSaving(false)
    }
  }

  const fetchDemandBreakdown = useCallback(async () => {
    if (demandBreakdown || breakdownLoading) return
    setBreakdownLoading(true)
    const { data } = await supabase.rpc('get_demand_breakdown_by_customer', {
      p_week_start: weekStart,
      p_family_id: familyId
    })
    setDemandBreakdown(data || [])
    setBreakdownLoading(false)
  }, [weekStart, familyId, demandBreakdown, breakdownLoading])

  return (
    <Card
      className={cn(
        'p-4 transition-all duration-200 group',
        hasGap && 'border-amber-300 bg-amber-50/30',
        !hasGap && !hasSurplus && 'border-green-300 bg-green-50/30',
        hasSurplus && 'border-blue-300 bg-blue-50/30'
      )}
    >
      <div className="space-y-3">
        {/* Header: Family name and code */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-gray-900">
                {familyCode}
              </h4>
              {hasGap && (
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              )}
              {!hasGap && !hasSurplus && (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
              {override && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  Override
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-0.5">{familyName}</p>
          </div>

          <div className="flex items-center gap-1">
            {/* Override button — visible on hover */}
            <Popover open={overrideOpen} onOpenChange={setOverrideOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenOverride}
                  className={cn(
                    'h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity',
                    override && 'opacity-100'
                  )}
                  title="Override demand"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-semibold text-gray-900">Override Demand</h5>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => setOverrideOpen(false)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>

                  <div className="text-xs text-gray-500">
                    Forecast: {demandKg.toFixed(1)}kg
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Override (kg)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={overrideKg}
                      onChange={(e) => setOverrideKg(e.target.value)}
                      className="h-8 text-sm"
                      placeholder="e.g. 25.0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700">
                      Reason (optional)
                    </label>
                    <Input
                      type="text"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      className="h-8 text-sm"
                      placeholder="e.g. Large order expected"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={handleSaveOverride}
                      disabled={saving || !overrideKg}
                      className="flex-1 h-7 text-xs"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    {override && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleRemoveOverride}
                        disabled={saving}
                        className="h-7 text-xs"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {batchCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-6 px-2"
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Metrics */}
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <HoverCard openDelay={200} onOpenChange={(open) => open && fetchDemandBreakdown()}>
              <HoverCardTrigger asChild>
                <span className="text-gray-600 hover:underline cursor-default">Demand:</span>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-60 p-3">
                <div className="space-y-2">
                  <h5 className="text-xs font-semibold text-gray-900">Demand Breakdown</h5>
                  {breakdownLoading ? (
                    <p className="text-xs text-gray-500">Loading...</p>
                  ) : demandBreakdown?.length > 0 ? (
                    <div className="space-y-1.5">
                      {demandBreakdown.map(row => (
                        <div key={row.customer_id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-gray-700 truncate">{row.customer_name}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="font-medium text-gray-900">{Number(row.total_kg).toFixed(1)}kg</span>
                            {Number(row.recurring_kg) > 0 && (
                              <RotateCw className="h-3 w-3 text-blue-500" title="Recurring order" />
                            )}
                            {Number(row.ai_kg) > 0 && (
                              <Sparkles className="h-3 w-3 text-purple-500" title="AI predicted" />
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between pt-1.5 border-t text-xs">
                        <span className="font-medium text-gray-700">Total</span>
                        <span className="font-semibold text-gray-900">
                          {demandBreakdown.reduce((sum, r) => sum + Number(r.total_kg), 0).toFixed(1)}kg
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">No customer data</p>
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>
            <span className={cn('font-medium', override ? 'text-purple-700' : 'text-gray-900')}>
              {effectiveDemandKg.toFixed(1)}kg
              {override && (
                <span className="text-gray-400 line-through ml-1 text-[10px]">
                  {demandKg.toFixed(1)}
                </span>
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Supply:</span>
            <span className="font-medium text-gray-900">{supplyKg.toFixed(1)}kg</span>
          </div>
          <div className="flex justify-between pt-1 border-t">
            <span className="text-gray-700 font-medium">
              {hasGap ? 'Gap:' : hasSurplus ? 'Surplus:' : 'Balanced:'}
            </span>
            <span
              className={cn(
                'font-semibold',
                hasGap && 'text-amber-700',
                hasSurplus && 'text-blue-700',
                !hasGap && !hasSurplus && 'text-green-700'
              )}
            >
              {Math.abs(effectiveGapKg).toFixed(1)}kg
            </span>
          </div>
        </div>

        {/* Override reason tooltip */}
        {override?.reason && (
          <p className="text-[10px] text-purple-600 italic truncate" title={override.reason}>
            {override.reason}
          </p>
        )}

        {/* Batch counts */}
        <div className="flex items-center gap-2 text-xs">
          {shadowBatches.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {shadowBatches.length} shadow
            </Badge>
          )}
          {realBatches.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {realBatches.reduce((sum, b) => sum + (b.total_blocks || 0), 0)} blocks
            </Badge>
          )}
          {batchCount === 0 && (
            <span className="text-gray-500 italic">No batches</span>
          )}
        </div>

        {/* Add batch button */}
        {hasGap && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddBatch?.(familyData)}
            className="w-full h-7 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Batch
          </Button>
        )}

        {/* Expanded: Show batches grouped by strain */}
        {isExpanded && batchCount > 0 && (
          <div className="space-y-3 pt-2 border-t">
            {Object.entries(batchesByStrain).map(([strainName, batches]) => (
              <div key={strainName} className="space-y-1.5">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  {strainName}
                </p>
                {batches.map(batch => (
                  <BatchCard
                    key={batch.id}
                    batch={batch}
                    onUpdate={onUpdate}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
